import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { GiftRanking } from './GiftRanking';
import { AIGiftSuggestions } from './AIGiftSuggestions';
import { GiftSuggestions } from './GiftSuggestions';
import { GiftGroupContribution } from './GiftGroupContribution';
import { 
  Calendar, 
  Users, 
  DollarSign, 
  Gift, 
  Clock,
  Loader2,
  AlertCircle,
  Sparkles,
  Edit,
  List,
  LayoutGrid,
  UserPlus,
  Trash2,
  ArrowUpDown,
  MapPin,
  CheckCircle,
  XCircle,
  Send
} from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

interface GiftGroupOverviewProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; tab?: string }) => void;
  giftGroupId: string | null;
  initialView?: ViewMode;
  initialSuggestionId?: string | null;
  initialOpenComment?: boolean;
}

interface Member {
  id: string;
  user_id: string | null;
  contact: string;
  role: string;
  status: string;
  workflow_state?: string;
  contributed_amount: number;
  invited_at: string;
  accepted_at: string | null;
  workflow_state_updated_at?: string | null;
  name?: string;
  email?: string;
  phone?: string;
}

interface Suggestion {
  id: string;
  title: string;
  description: string;
  url: string;
  price: number;
  image_url?: string;
  suggested_by_user_id: string;
  suggested_by_email?: string;
  status: string;
  created_at: string;
}

interface GiftGroupData {
  id: string;
  title: string;
  type_id: number;
  type_name: string;
  type_description: string;
  creator_id: string;
  gift_date: string;
  order_by_date: string;
  recommended_contribution: number | null;
  status: string;
  total_pool: number;
  created_at: string;
  updated_at: string;
  members: Member[];
  suggestions: Suggestion[];
  currentUserRole: string;
  currentUserStatus: string;
}

type ViewMode = 'ranking' | 'ai-suggestions' | 'manual-suggestions' | 'contribute';
type TabValue = 'ranking' | 'members' | 'activity';

interface Activity {
  id: string;
  type: string;
  message: string;
  createdAt: string;
  userName: string;
}

export function GiftGroupOverview({ onNavigate, giftGroupId, initialView, initialSuggestionId, initialOpenComment }: GiftGroupOverviewProps) {
  const [giftGroup, setGiftGroup] = useState<GiftGroupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ViewMode>(initialView || 'ranking');
  const [activeTab, setActiveTab] = useState<TabValue>('ranking');
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesSortAsc, setActivitiesSortAsc] = useState(false); // false = newest first (desc), true = oldest first (asc)
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [newMemberContact, setNewMemberContact] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ id: string; contact: string } | null>(null);
  const [removingMember, setRemovingMember] = useState(false);
  const [resendingInvite, setResendingInvite] = useState<string | null>(null);
  const [shippingAddressDialog, setShippingAddressDialog] = useState<{ memberId: string; name: string } | null>(null);
  const [shippingAddressData, setShippingAddressData] = useState<any>(null);
  const [loadingShippingAddress, setLoadingShippingAddress] = useState(false);
  const [editingShippingAddress, setEditingShippingAddress] = useState(false);
  const [editAddressForm, setEditAddressForm] = useState({
    name: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  });
  const [savingShippingAddress, setSavingShippingAddress] = useState(false);

  useEffect(() => {
    if (!giftGroupId) {
      setError('No gift group selected');
      setLoading(false);
      return;
    }

    const fetchGiftGroup = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getGiftGroup(giftGroupId);
        setGiftGroup(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load gift group';
        setError(errorMessage);
        toast.error('Failed to load gift group', {
          description: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchGiftGroup();
  }, [giftGroupId]);

  const fetchActivities = async () => {
    if (!giftGroupId) return;
    
    try {
      setActivitiesLoading(true);
      const data = await api.getGiftGroupActivities(giftGroupId);
      setActivities(data.activities || []);
    } catch (err) {
      console.error('Failed to load activities:', err);
      toast.error('Failed to load activities');
    } finally {
      setActivitiesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'activity') {
      fetchActivities();
    }
  }, [activeTab, giftGroupId]);

  const handleAddMember = async () => {
    if (!newMemberContact.trim() || !giftGroupId) return;

    try {
      setAddingMember(true);
      await api.addGiftGroupMember(giftGroupId, newMemberContact.trim());
      
      toast.success('Member added!', {
        description: `Invitation sent to ${newMemberContact}`,
      });

      // Refresh gift group data
      const data = await api.getGiftGroup(giftGroupId);
      setGiftGroup(data);

      // Close dialog and reset form
      setShowAddMemberDialog(false);
      setNewMemberContact('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add member';
      toast.error('Failed to add member', {
        description: errorMessage,
      });
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!giftGroupId || !memberToRemove) return;

    try {
      setRemovingMember(true);
      await api.removeGiftGroupMember(giftGroupId, memberToRemove.id);
      
      toast.success('Member removed', {
        description: `${memberToRemove.contact} has been removed from the gift group`,
      });

      // Refresh gift group data
      const data = await api.getGiftGroup(giftGroupId);
      setGiftGroup(data);
      setMemberToRemove(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove member';
      toast.error('Failed to remove member', {
        description: errorMessage,
      });
    } finally {
      setRemovingMember(false);
    }
  };

  const handleResendInvite = async (memberId: string, contact: string) => {
    if (!giftGroupId) return;

    try {
      setResendingInvite(memberId);
      await api.resendGiftGroupInvitation(giftGroupId, memberId);
      
      toast.success('Invitation resent!', {
        description: `Invitation has been resent to ${contact}`,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resend invitation';
      toast.error('Failed to resend invitation', {
        description: errorMessage,
      });
    } finally {
      setResendingInvite(null);
    }
  };

  const handleViewShippingAddress = async (memberId: string, name: string) => {
    if (!giftGroupId) return;

    try {
      setLoadingShippingAddress(true);
      setShippingAddressDialog({ memberId, name });
      const data = await api.getMemberShippingAddress(giftGroupId, memberId);
      setShippingAddressData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load shipping address';
      toast.error('Failed to load shipping address', {
        description: errorMessage,
      });
      setShippingAddressDialog(null);
    } finally {
      setLoadingShippingAddress(false);
    }
  };

  const handleEditShippingAddress = () => {
    setEditingShippingAddress(true);
    setEditAddressForm({
      name: shippingAddressData?.address?.name || shippingAddressData?.name || '',
      phone: shippingAddressData?.address?.phone || '',
      street: shippingAddressData?.address?.street || '',
      city: shippingAddressData?.address?.city || '',
      state: shippingAddressData?.address?.state || '',
      postalCode: shippingAddressData?.address?.postalCode || '',
      country: shippingAddressData?.address?.country || 'USA',
    });
  };

  const handleSaveShippingAddress = async () => {
    if (!giftGroupId || !shippingAddressDialog) return;

    try {
      setSavingShippingAddress(true);
      await api.updateShippingAddress(giftGroupId, {
        targetMemberId: shippingAddressDialog.memberId,
        address: editAddressForm,
        skip: false,
      });

      toast.success('Address updated successfully');
      
      // Reload the address data
      const data = await api.getMemberShippingAddress(giftGroupId, shippingAddressDialog.memberId);
      setShippingAddressData(data);
      setEditingShippingAddress(false);
      
      // Refresh gift group data to update the UI
      const groupData = await api.getGiftGroup(giftGroupId);
      setGiftGroup(groupData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save address';
      toast.error('Failed to save address', {
        description: errorMessage,
      });
    } finally {
      setSavingShippingAddress(false);
    }
  };

  const handleConfirmShippingAddress = async () => {
    if (!giftGroupId || !shippingAddressDialog) return;

    try {
      setSavingShippingAddress(true);
      await api.confirmShippingAddress(giftGroupId, shippingAddressDialog.memberId);

      toast.success('Address confirmed');
      
      // Close dialog and refresh
      setShippingAddressDialog(null);
      const groupData = await api.getGiftGroup(giftGroupId);
      setGiftGroup(groupData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to confirm address';
      toast.error('Failed to confirm address', {
        description: errorMessage,
      });
    } finally {
      setSavingShippingAddress(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="p-8 border border-primary/20 shadow-lg">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading gift group...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !giftGroup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="p-8 border border-primary/20 shadow-lg max-w-md">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error || 'Gift group not found'}</AlertDescription>
          </Alert>
          <Button
            className="mt-4 w-full bg-primary hover:bg-primary/90"
            onClick={() => onNavigate('dashboard', { tab: 'todo' })}
          >
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  // Calculate metrics
  const gifters = giftGroup.members.filter(m => m.role === 'gifter');
  const acceptedGifters = gifters.filter(m => m.status === 'accepted');
  const totalContributed = gifters.reduce((sum, m) => sum + m.contributed_amount, 0);
  const targetPool = (giftGroup.recommended_contribution || 0) * acceptedGifters.length;
  const percentContributed = targetPool > 0 ? (totalContributed / targetPool) * 100 : 0;

  const formatDate = (dateString: string) => {
    // Handle both ISO timestamps and YYYY-MM-DD dates
    if (!dateString) return 'N/A';
    
    // If it's an ISO timestamp (contains 'T'), convert it with date and time
    if (dateString.includes('T')) {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    
    // Otherwise parse YYYY-MM-DD as local date, not UTC (no time for date-only strings)
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Card className="p-6 border border-primary/20 shadow-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-wrap">
              <Badge variant="secondary">{giftGroup.type_name}</Badge>
              <h1 className="text-primary mb-0">{giftGroup.title}</h1>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>Gift Date: {formatDate(giftGroup.gift_date)}</span>
            </div>
          </div>
        </Card>

        {/* Status Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <Card className="p-3 border border-primary/20 shadow-lg">
            <div className="flex flex-col gap-y-1">
              <div className="flex items-center justify-between">
                <h3 className="text-xs text-muted-foreground uppercase leading-none">
                  {giftGroup.type_id === 5 ? 'Your Gift Budget' : 
                   giftGroup.type_id === 4 ? 'Exchange Budget' : 'Gift Pool Status'}
                </h3>
                <DollarSign className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="text-primary leading-none">{formatCurrency(totalContributed)}</div>
              <Progress value={percentContributed} className="h-1.5" />
              <div className="text-xs text-muted-foreground leading-none">
                {giftGroup.type_id === 5 
                  ? `${percentContributed.toFixed(0)}% of ${formatCurrency(giftGroup.recommended_contribution || 0)} suggested`
                  : giftGroup.type_id === 4
                  ? `${percentContributed.toFixed(0)}% of ${formatCurrency(targetPool)} target (${formatCurrency(giftGroup.recommended_contribution || 0)}/person)`
                  : `${percentContributed.toFixed(0)}% of ${formatCurrency(targetPool)} target`}
              </div>
            </div>
          </Card>

          <Card className="p-3 border border-primary/20 shadow-lg">
            <div className="flex flex-col gap-y-1">
              <div className="flex items-center justify-between">
                <h3 className="text-xs text-muted-foreground uppercase leading-none">Participation</h3>
                <Users className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="text-primary leading-none">{acceptedGifters.length}/{gifters.length}</div>
              <Progress value={(acceptedGifters.length / gifters.length) * 100} className="h-1.5" />
              <div className="text-xs text-muted-foreground leading-none">
                {gifters.length - acceptedGifters.length} pending
              </div>
            </div>
          </Card>

          <Card className="p-3 border border-primary/20 shadow-lg">
            <div className="flex flex-col gap-y-1">
              <div className="flex items-center justify-between">
                <h3 className="text-xs text-muted-foreground uppercase leading-none">Gift Status</h3>
                <Gift className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="text-primary leading-none">{giftGroup.suggestions.length} suggestions</div>
              <div className="text-xs text-muted-foreground leading-none">
                Status: {giftGroup.status.replace('_', ' ').charAt(0).toUpperCase() + giftGroup.status.slice(1)}
              </div>
            </div>
          </Card>
        </div>

        {/* Info Panel - Now visible in all tabs */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card className="p-3 bg-blue-50 border-blue-200 border-2">
            <div className="flex flex-col gap-y-1">
              <h4 className="text-primary leading-none">
                {giftGroup.type_id === 5 ? 'White Elephant Rules' :
                 giftGroup.type_id === 4 ? 'Gift Exchange Rules' :
                 'How Ranking Works'}
              </h4>
              <ul className="text-sm text-muted-foreground space-y-0.5">
                {giftGroup.type_id === 5 ? (
                  <>
                    <li>• You suggest the gift YOU will bring</li>
                    <li>• Your contribution pays for your gift</li>
                    <li>• During the event, gifts are picked or stolen</li>
                    <li>• Each gift can be stolen up to 3 times</li>
                  </>
                ) : giftGroup.type_id === 4 ? (
                  <>
                    <li>• You'll be randomly paired with another member</li>
                    <li>• Suggest gifts for anyone except yourself</li>
                    <li>• You can only rank your assigned person's gifts</li>
                    <li>• Your contribution goes toward buying your gift</li>
                  </>
                ) : (
                  <>
                    <li>• Your ranking influences the group ranking</li>
                    <li>• Group ranking determines purchase order</li>
                    <li>• Gifts within budget are prioritized</li>
                    <li>• Order by date affects when gifts are purchased</li>
                  </>
                )}
              </ul>
            </div>
          </Card>

          <Card className="p-3 bg-green-50 border-green-200 border-2">
            <div className="flex flex-col gap-y-1">
              <h4 className="text-primary leading-none">
                {giftGroup.type_id === 5 ? 'How It Works' :
                 giftGroup.type_id === 4 ? 'How It Works' :
                 'Purchase Logic'}
              </h4>
              <p className="text-sm text-muted-foreground leading-snug">
                {giftGroup.type_id === 5 
                  ? 'Each member contributes to buy their own gift. During the pick/steal event, gifts are revealed and members take turns picking or stealing gifts.'
                  : giftGroup.type_id === 4
                  ? 'Once paired, each member buys a gift for their assigned person. Contributions go toward the gift YOU will receive. The highest ranked gift within budget will be purchased.'
                  : 'Better to Give than Receive will automatically purchase the highest group-ranked gift that\'s within budget and can arrive by the gift date. Multiple gifts can be purchased until the pool is depleted.'}
              </p>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)} className="space-y-4">
          {/* Tabs and Action Buttons on Same Line */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className={`border-2 ${activeTab === 'ranking' ? 'border-primary bg-primary text-white hover:bg-primary/90' : 'border-primary/30 text-primary hover:bg-primary/10'}`}
                onClick={() => {
                  setActiveTab('ranking');
                  setActiveView('ranking');
                }}
              >
                <LayoutGrid className="w-4 h-4 mr-1.5" />
                Gift Rankings
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={`border-2 ${activeTab === 'members' ? 'border-primary bg-primary text-white hover:bg-primary/90' : 'border-primary/30 text-primary hover:bg-primary/10'}`}
                onClick={() => setActiveTab('members')}
              >
                <Users className="w-4 h-4 mr-1.5" />
                Members
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={`border-2 ${activeTab === 'activity' ? 'border-primary bg-primary text-white hover:bg-primary/90' : 'border-primary/30 text-primary hover:bg-primary/10'}`}
                onClick={() => setActiveTab('activity')}
              >
                <List className="w-4 h-4 mr-1.5" />
                Activity
              </Button>
            </div>

            {/* Action Buttons - Only show on Rankings tab */}
            {activeTab === 'ranking' && (
              <div className="flex items-center gap-2 flex-wrap">
                {/* White Elephant Game Button - Only for type 5 */}
                {giftGroup.type_id === 5 && (
                  <Button 
                    size="sm" 
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => onNavigate('white-elephant-game', { giftGroupId: giftGroup.id })}
                  >
                    <Gift className="w-4 h-4 mr-1.5" />
                    Play White Elephant
                  </Button>
                )}
                {/* Back to Rankings button - shown when not on ranking view */}
                {activeView !== 'ranking' && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-2 border-primary/30 text-primary hover:bg-primary/10"
                    onClick={() => setActiveView('ranking')}
                  >
                    <LayoutGrid className="w-4 h-4 mr-1.5" />
                    Back to Rankings
                  </Button>
                )}
                {/* Contribute button - shown when not on contribute view */}
                {activeView !== 'contribute' && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-2 border-primary/30 text-primary hover:bg-primary/10"
                    onClick={() => setActiveView('contribute')}
                  >
                    <DollarSign className="w-4 h-4 mr-1.5" />
                    Contribute
                  </Button>
                )}
                {/* AI Gift Ideas button - shown when not on ai-suggestions view */}
                {activeView !== 'ai-suggestions' && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-2 border-primary/30 text-primary hover:bg-primary/10"
                    onClick={() => setActiveView('ai-suggestions')}
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    AI Gift Ideas
                  </Button>
                )}
                {/* Add Manual Gifts button - shown when not on manual-suggestions view */}
                {activeView !== 'manual-suggestions' && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-2 border-primary/30 text-primary hover:bg-primary/10"
                    onClick={() => setActiveView('manual-suggestions')}
                  >
                    <Edit className="w-4 h-4 mr-1.5" />
                    Add Manual Gifts
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Rankings Tab */}
          <TabsContent value="ranking" className="mt-0">
            <div className="space-y-4">
              
              {/* Dynamic Content Based on View */}
              {activeView === 'ranking' && (
                <GiftRanking 
                  onNavigate={onNavigate} 
                  giftGroupId={giftGroup.id}
                  embedded={true}
                  initialSuggestionId={initialSuggestionId}
                  initialOpenComment={initialOpenComment}
                />
              )}
              
              {activeView === 'ai-suggestions' && (
                <AIGiftSuggestions 
                  onNavigate={onNavigate} 
                  giftGroupId={giftGroup.id}
                  embedded={true}
                  onSuggestionsAdded={() => setActiveView('ranking')}
                />
              )}
              
              {activeView === 'manual-suggestions' && (
                <GiftSuggestions 
                  onNavigate={onNavigate} 
                  giftGroupId={giftGroup.id}
                  embedded={true}
                />
              )}
              
              {activeView === 'contribute' && (
                <GiftGroupContribution 
                  onNavigate={onNavigate} 
                  giftGroupId={giftGroup.id}
                  embedded={true}
                  onContributionComplete={() => setActiveView('ranking')}
                />
              )}
            </div>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members">
            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20 flex items-center justify-between">
                <h2 className="text-primary">Gift Group Members</h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddMemberDialog(true)}
                    className="border-primary/30 hover:bg-primary/5"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Gifter
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name/Contact</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Contributed</TableHead>
                    <TableHead>Shipping</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {giftGroup.members.map((member) => {
                    // Helper function to format workflow state for display
                    const formatWorkflowState = (state: string | undefined) => {
                      if (!state) return 'Pending Acceptance';
                      
                      // Map workflow states to display names
                      const stateMap: Record<string, string> = {
                        'pending_acceptance': 'Pending Acceptance',
                        'declined': 'Declined',
                        'pending_contribution': 'Pending Contribution',
                        'pending_shipping_address': 'Pending Shipping Address',
                        'pending_ai_gifts': 'Pending AI Gifts',
                        'pending_manual_gifts': 'Pending Manual Gifts',
                        'pending_ranking': 'Pending Ranking',
                        'pending_order': 'Pending Order',
                        'pending_delivery': 'Pending Delivery',
                        'pending_confirmation': 'Pending Confirmation',
                        'removed': 'Removed',
                        'active': 'Active'
                      };
                      
                      return stateMap[state] || state.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    };

                    // Determine badge styling based on workflow state
                    const getStatusStyle = (workflowState: string | undefined) => {
                      if (!workflowState) return 'bg-red-100 text-red-800 border-red-300';
                      
                      switch (workflowState) {
                        case 'active':
                          return 'bg-green-100 text-green-800 border-green-300';
                        case 'pending_delivery':
                        case 'pending_confirmation':
                          return 'bg-blue-100 text-blue-800 border-blue-300';
                        case 'declined':
                        case 'removed':
                          return 'bg-red-100 text-red-800 border-red-300';
                        case 'pending_acceptance':
                          return 'bg-yellow-100 text-yellow-800 border-yellow-300';
                        case 'pending_contribution':
                          return 'bg-orange-100 text-orange-800 border-orange-300';
                        case 'pending_shipping_address':
                          return 'bg-teal-100 text-teal-800 border-teal-300';
                        case 'pending_ai_gifts':
                        case 'pending_manual_gifts':
                          return 'bg-purple-100 text-purple-800 border-purple-300';
                        case 'pending_ranking':
                          return 'bg-indigo-100 text-indigo-800 border-indigo-300';
                        case 'pending_order':
                          return 'bg-cyan-100 text-cyan-800 border-cyan-300';
                        default:
                          return 'bg-gray-100 text-gray-800 border-gray-300';
                      }
                    };

                    return (
                      <TableRow key={member.id}>
                        <TableCell>{member.name || member.email || member.phone || member.contact}</TableCell>
                        <TableCell>
                          <Badge className={member.role === 'gifter' ? 'bg-primary/10 text-primary border-primary/30' : 'bg-secondary/50 text-secondary-foreground border-secondary'}>
                            {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {member.role === 'giftee' ? (
                            <Badge className="bg-green-100 text-green-800 border-green-300">
                              Active
                            </Badge>
                          ) : (
                            <Badge className={getStatusStyle(member.workflow_state)}>
                              {formatWorkflowState(member.workflow_state)}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {member.role === 'gifter' ? formatCurrency(member.contributed_amount) : '-'}
                        </TableCell>
                        <TableCell>
                          {(member.role === 'giftee' || member.role === 'gifter-giftee') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewShippingAddress(member.id, member.name || member.email || member.phone || member.contact)}
                              className="p-1 h-auto hover:bg-primary/5"
                            >
                              <MapPin className="w-4 h-4 text-primary" />
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          {member.role === 'giftee' ? '-' : (
                            member.workflow_state_updated_at 
                              ? formatDate(member.workflow_state_updated_at) 
                              : (member.accepted_at ? formatDate(member.accepted_at) : 'Pending')
                          )}
                         </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Resend Invite button - only show for pending gifters (not giftees) */}
                            {member.workflow_state === 'pending_acceptance' && member.role !== 'giftee' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResendInvite(member.id, member.name || member.email || member.phone || member.contact)}
                                disabled={resendingInvite === member.id}
                                className="text-primary hover:text-primary/80 hover:bg-primary/10"
                                title="Resend Invite"
                              >
                                {resendingInvite === member.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Send className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {/* Remove button - only show for members with no contribution */}
                            {member.contributed_amount === 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setMemberToRemove({ id: member.id, contact: member.name || member.email || member.phone || member.contact })}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Remove Member"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20 flex items-center justify-between">
                <h2 className="text-primary">Recent Activity</h2>
                {activities.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActivitiesSortAsc(!activitiesSortAsc)}
                    className="border-primary/30 hover:bg-primary/5 gap-2"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                    {activitiesSortAsc ? 'Oldest First' : 'Newest First'}
                  </Button>
                )}
              </div>
              {activitiesLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-primary" />
                  <p className="text-muted-foreground">Loading activities...</p>
                </div>
              ) : activities.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No activity yet</p>
                  <p className="text-sm mt-1">Activity will appear here as members interact with the gift group</p>
                </div>
              ) : (
                <div className="p-4">
                  {[...activities]
                    .sort((a, b) => {
                      const dateA = new Date(a.createdAt).getTime();
                      const dateB = new Date(b.createdAt).getTime();
                      return activitiesSortAsc ? dateA - dateB : dateB - dateA;
                    })
                    .map((activity) => {
                      // Format date and time in browser's local timezone
                      console.log('Activity timestamp raw:', activity.createdAt);
                      const date = new Date(activity.createdAt);
                      console.log('Activity date object:', date);
                      console.log('Activity date ISO:', date.toISOString());
                      console.log('Activity date timezone offset:', date.getTimezoneOffset());
                      
                      const dateStr = date.toLocaleDateString(undefined, { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      });
                      const timeStr = date.toLocaleTimeString(undefined, { 
                        hour: 'numeric', 
                        minute: '2-digit', 
                        hour12: true 
                      });
                      
                      console.log('Activity formatted date:', dateStr, timeStr);
                      
                      return (
                        <div key={activity.id} className="flex items-center gap-3 py-2 border-b border-primary/10 last:border-0 text-sm">
                          <span className="text-muted-foreground whitespace-nowrap">
                            {dateStr} {timeStr}
                          </span>
                          <span className="text-muted-foreground">•</span>
                          <span className="flex-1">{activity.message}</span>
                        </div>
                      );
                    })}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Gifter</DialogTitle>
            <DialogDescription>
              Enter the email address or phone number of the person you want to invite to this gift group.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="contact">Email or Phone</Label>
              <Input
                id="contact"
                type="text"
                placeholder="gifter@example.com or +1234567890"
                value={newMemberContact}
                onChange={(e) => setNewMemberContact(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !addingMember) {
                    handleAddMember();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddMemberDialog(false);
                setNewMemberContact('');
              }}
              disabled={addingMember}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMember}
              disabled={!newMemberContact.trim() || addingMember}
              className="bg-primary hover:bg-primary/90"
            >
              {addingMember ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Gifter
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <AlertDialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Gifter</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <span className="font-semibold">{memberToRemove?.contact}</span> from this gift group?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingMember}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={removingMember}
            >
              {removingMember ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Shipping Address View Dialog */}
      <Dialog open={!!shippingAddressDialog} onOpenChange={(open) => !open && setShippingAddressDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Shipping Address
            </DialogTitle>
            <DialogDescription>
              Address information for {shippingAddressDialog?.name}
            </DialogDescription>
          </DialogHeader>
          
          {loadingShippingAddress ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : shippingAddressData ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-secondary/20 rounded-lg">
                {shippingAddressData.hasAddress ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium">Address on file</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-orange-600" />
                    <span className="text-sm font-medium">No address provided yet</span>
                  </>
                )}
              </div>

              {editingShippingAddress ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-name">Recipient Name</Label>
                      <Input
                        id="edit-name"
                        value={editAddressForm.name}
                        onChange={(e) => setEditAddressForm({ ...editAddressForm, name: e.target.value })}
                        placeholder="John Doe"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-phone">Contact Phone</Label>
                      <Input
                        id="edit-phone"
                        type="tel"
                        value={editAddressForm.phone}
                        onChange={(e) => setEditAddressForm({ ...editAddressForm, phone: e.target.value })}
                        placeholder="+1-555-0100"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit-street">Street Address</Label>
                    <Input
                      id="edit-street"
                      value={editAddressForm.street}
                      onChange={(e) => setEditAddressForm({ ...editAddressForm, street: e.target.value })}
                      placeholder="123 Main Street, Apt 4B"
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-city">City</Label>
                      <Input
                        id="edit-city"
                        value={editAddressForm.city}
                        onChange={(e) => setEditAddressForm({ ...editAddressForm, city: e.target.value })}
                        placeholder="Springfield"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-state">State / Province</Label>
                      <Input
                        id="edit-state"
                        value={editAddressForm.state}
                        onChange={(e) => setEditAddressForm({ ...editAddressForm, state: e.target.value })}
                        placeholder="IL"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-postalCode">ZIP / Postal Code</Label>
                      <Input
                        id="edit-postalCode"
                        value={editAddressForm.postalCode}
                        onChange={(e) => setEditAddressForm({ ...editAddressForm, postalCode: e.target.value })}
                        placeholder="62701"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-country">Country</Label>
                      <Input
                        id="edit-country"
                        value={editAddressForm.country}
                        onChange={(e) => setEditAddressForm({ ...editAddressForm, country: e.target.value })}
                        placeholder="USA"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {shippingAddressData.hasAddress && shippingAddressData.address && (
                    <div className="space-y-2 p-4 border border-primary/20 rounded-lg">
                      {shippingAddressData.address.name && (
                        <p className="text-sm">
                          <span className="font-medium">Name:</span> {shippingAddressData.address.name}
                        </p>
                      )}
                      {shippingAddressData.address.phone && (
                        <p className="text-sm">
                          <span className="font-medium">Phone:</span> {shippingAddressData.address.phone}
                        </p>
                      )}
                      {shippingAddressData.address.street && (
                        <p className="text-sm">
                          <span className="font-medium">Street:</span> {shippingAddressData.address.street}
                        </p>
                      )}
                      {shippingAddressData.address.city && (
                        <p className="text-sm">
                          <span className="font-medium">City:</span> {shippingAddressData.address.city}
                        </p>
                      )}
                      {shippingAddressData.address.state && (
                        <p className="text-sm">
                          <span className="font-medium">State:</span> {shippingAddressData.address.state}
                        </p>
                      )}
                      {shippingAddressData.address.postalCode && (
                        <p className="text-sm">
                          <span className="font-medium">Postal Code:</span> {shippingAddressData.address.postalCode}
                        </p>
                      )}
                      {shippingAddressData.address.country && (
                        <p className="text-sm">
                          <span className="font-medium">Country:</span> {shippingAddressData.address.country}
                        </p>
                      )}
                      {!shippingAddressData.address.street && !shippingAddressData.address.city && 
                       !shippingAddressData.address.state && !shippingAddressData.address.postalCode && (
                        <p className="text-sm text-muted-foreground italic">Partial address - some fields not provided</p>
                      )}
                    </div>
                  )}

                  {shippingAddressData.enteredByName && (
                    <p className="text-xs text-muted-foreground">
                      Entered by: {shippingAddressData.enteredByName}
                    </p>
                  )}
                </>
              )}
            </div>
          ) : null}

          <DialogFooter>
            {editingShippingAddress ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setEditingShippingAddress(false)}
                  disabled={savingShippingAddress}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveShippingAddress}
                  disabled={savingShippingAddress}
                  className="bg-primary hover:bg-primary/90"
                >
                  {savingShippingAddress ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Address'
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShippingAddressDialog(null)}
                >
                  Close
                </Button>
                {shippingAddressData?.hasAddress  && (
                  <Button
                    onClick={handleConfirmShippingAddress}
                    disabled={savingShippingAddress}
                    variant="outline"
                    className="border-green-600 text-green-600 hover:bg-green-50"
                  >
                    {savingShippingAddress ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Confirming...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Confirm Address
                      </>
                    )}
                  </Button>
                )}
                <Button
                  onClick={handleEditShippingAddress}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Address
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
